import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import Button from '@/components/ui/Button';
import Select from '@/components/ui/Select';
import Icon from '@/components/AppIcon';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogBody, DialogFooter } from '@/components/ui/Dialog';
import type { LeavePolicyView, LeavePolicyInput, StatutoryLeaveTemplateView } from '../../../../../models/attendance';
import type { Country } from '../../../../../models/general';
import { EmploymentType } from '../../../../../models/attendance';
import { getStatutoryTemplatesByCountry } from '../../../../../api/Attendance/leaves';
import { getCountries } from '../../../../../api/General/Country';

interface PolicyFormErrors {
  name?: string;
  legalEntity?: string;
  country?: string;
}

interface PolicyFormModalProps {
  isOpen: boolean;
  onClose: () => void;
  editingPolicy: LeavePolicyView | null;
  onSave: (input: LeavePolicyInput) => void;
  isSaving?: boolean;
  companyId?: number;
}

const PolicyFormModal: React.FC<PolicyFormModalProps> = ({ isOpen, onClose, editingPolicy, onSave, isSaving, companyId }) => {
  const { t } = useTranslation('leaves');
  const companyName = useSelector((state: any) => state.user?.companyName);
  const [apiCountries, setApiCountries] = useState<Country[]>([]);
  const [formData, setFormData] = useState<LeavePolicyInput>({
    name: '',
    description: '',
    legalEntity: '',
    country: '',
    employmentType: EmploymentType.All,
    isDefault: false,
    monthlyShortLeaveHoursLimit: 0,
    maxShortLeaveHoursPerRequest: 0,
    shortLeaveNoticePeriodHours: 0,
    shortLeaveRequiresApproval: true,
    shortLeaveWindowStart: undefined,
    shortLeaveWindowEnd: undefined
  });
  const [errors, setErrors] = useState<PolicyFormErrors>({});
  const [statutoryTemplates, setStatutoryTemplates] = useState<StatutoryLeaveTemplateView[]>([]);
  const [isLoadingTemplates, setIsLoadingTemplates] = useState(false);

  // ── Fetch countries from API ──
  useEffect(() => {
    getCountries().then(result => {
      if (!result?.errors?.length) setApiCountries(result.payload ?? []);
    }).catch(() => {});
  }, []);

  const fetchStatutoryTemplates = useCallback(async (countryIso: string) => {
    if (!countryIso) {
      setStatutoryTemplates([]);
      return;
    }
    // Resolve ISO code to countryId
    const country = apiCountries.find(c => c.iso === countryIso);
    if (!country) {
      setStatutoryTemplates([]);
      return;
    }
    setIsLoadingTemplates(true);
    try {
      const result = await getStatutoryTemplatesByCountry(country.id);
      if (!result?.errors?.length) {
        setStatutoryTemplates(result.payload ?? []);
      }
    } finally {
      setIsLoadingTemplates(false);
    }
  }, [apiCountries]);

  const handleApplyStatutoryDefaults = () => {
    // Statutory template defaults – no policy-level fields to apply currently
  };

  const legalEntityOptions = companyName ? [{ value: companyName, label: companyName }] : [];

  const countryOptions = apiCountries.map(c => ({ value: c.iso, label: c.nicename }));

  const employmentTypeOptions = [
    { value: EmploymentType.All, label: t('All Employment Types') },
    { value: EmploymentType.FullTime, label: t('Full-time') },
    { value: EmploymentType.PartTime, label: t('Part-time') },
    { value: EmploymentType.Contract, label: t('Contract') },
    { value: EmploymentType.Executive, label: t('Executive') }
  ];

  useEffect(() => {
    if (editingPolicy) {
      setFormData({
        name: editingPolicy.name || '',
        description: editingPolicy.description || '',
        legalEntity: editingPolicy.legalEntity || '',
        country: editingPolicy.country || '',
        employmentType: editingPolicy.employmentType || EmploymentType.All,
        isDefault: editingPolicy.isDefault || false,
        monthlyShortLeaveHoursLimit: editingPolicy.monthlyShortLeaveHoursLimit ?? 0,
        maxShortLeaveHoursPerRequest: editingPolicy.maxShortLeaveHoursPerRequest ?? 0,
        shortLeaveNoticePeriodHours: editingPolicy.shortLeaveNoticePeriodHours ?? 0,
        shortLeaveRequiresApproval: editingPolicy.shortLeaveRequiresApproval ?? true,
        shortLeaveWindowStart: editingPolicy.shortLeaveWindowStart ?? undefined,
        shortLeaveWindowEnd: editingPolicy.shortLeaveWindowEnd ?? undefined
      });
      if (editingPolicy.country) fetchStatutoryTemplates(editingPolicy.country);
    } else {
      setFormData({
        name: '', description: '',
        legalEntity: '', country: '',
        employmentType: EmploymentType.All,
        isDefault: false,
        monthlyShortLeaveHoursLimit: 0,
        maxShortLeaveHoursPerRequest: 0,
        shortLeaveNoticePeriodHours: 0,
        shortLeaveRequiresApproval: true,
        shortLeaveWindowStart: undefined,
        shortLeaveWindowEnd: undefined
      });
    }
    setErrors({});
  }, [editingPolicy, isOpen]);

  const validateForm = (): boolean => {
    const newErrors: PolicyFormErrors = {};
    if (!formData.name?.trim()) newErrors.name = t('Policy name is required');
    if (!formData.legalEntity) newErrors.legalEntity = t('Legal entity is required');
    if (!formData.country) newErrors.country = t('Country is required');
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (): void => {
    if (validateForm()) {
      onSave(formData);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={(open) => { if (!open) onClose(); }}>
      <DialogContent size="lg">
        <DialogHeader>
          <DialogTitle>
            {editingPolicy ? t('Edit Policy') : t('Create New Policy')}
          </DialogTitle>
          <p className="text-sm text-muted-foreground mt-1">{t('Define the scope and parameters for this leave policy')}</p>
        </DialogHeader>

        <DialogBody className="space-y-6">
          <div>
            <label className="block text-sm font-medium text-muted-foreground mb-2">
              {t('Policy Name')} <span className="text-destructive">*</span>
            </label>
            <input
              type="text"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              placeholder={t('e.g., UAE Standard Policy')}
              className={`w-full px-3 py-2 border rounded-md bg-card text-foreground focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus:outline-none ${errors.name ? 'border-destructive' : 'border-border'}`}
              data-testid="policy-name-input"
            />
            {errors.name && <p className="text-xs text-destructive mt-1">{errors.name}</p>}
          </div>

          <div>
            <label className="block text-sm font-medium text-muted-foreground mb-2">{t('Description')}</label>
            <textarea
              value={formData.description || ''}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              placeholder={t('Brief description of this policy')}
              rows={3}
              className="w-full px-3 py-2 border border-border rounded-md bg-card text-foreground focus-visible:ring-2 focus-visible:ring-primary/20 focus-visible:border-primary focus:outline-none"
            />
          </div>

          <div className="border border-primary/20 bg-primary/5 rounded-lg p-4">
            <h4 className="text-sm font-semibold text-foreground mb-4 flex items-center gap-2">
              <Icon name="Filter" size={16} />
              {t('Scope')}
            </h4>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-muted-foreground mb-2">
                  {t('Legal Entity')} <span className="text-destructive">*</span>
                </label>
                <Select
                  value={formData.legalEntity || ''}
                  onChange={(value: string) => setFormData({ ...formData, legalEntity: value })}
                  options={legalEntityOptions}
                  className={errors.legalEntity ? 'border-destructive' : ''}
                />
                {errors.legalEntity && <p className="text-xs text-destructive mt-1">{errors.legalEntity}</p>}
              </div>

              <div>
                <label className="block text-sm font-medium text-muted-foreground mb-2">
                  {t('Country')} <span className="text-destructive">*</span>
                </label>
                <Select
                  value={formData.country || ''}
                  onChange={(value: string) => {
                    setFormData({ ...formData, country: value });
                    fetchStatutoryTemplates(value);
                  }}
                  options={countryOptions}
                  className={errors.country ? 'border-destructive' : ''}
                />
                {errors.country && <p className="text-xs text-destructive mt-1">{errors.country}</p>}
              </div>

              {/* Statutory Templates Pre-fill Banner */}
              {statutoryTemplates.length > 0 && (
                <div className="bg-success/10 border border-success/20 rounded-lg p-3" data-testid="statutory-prefill-banner">
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center gap-2">
                      <Icon name="Scale" size={16} className="text-success" />
                      <span className="text-sm font-medium text-success">{t('Statutory Templates')}</span>
                    </div>
                    <Button variant="ghost" size="sm" onClick={handleApplyStatutoryDefaults} data-testid="apply-statutory-defaults-btn">
                      {t('Apply Template')}
                    </Button>
                  </div>
                  <p className="text-xs text-success/80 mb-2">{t('applyTemplateDescription')}</p>
                  <div className="space-y-1">
                    {statutoryTemplates.map(tmpl => (
                      <div key={tmpl.id} className="text-xs text-success/80 flex items-center gap-2">
                        <span className="font-medium">{tmpl.leaveTypeName}:</span>
                        <span>{tmpl.minDaysPerYear} {t('Min Days Per Year')}</span>
                        {tmpl.probationMonths > 0 && <span>&bull; {tmpl.probationMonths} {t('Probation Months')}</span>}
                      </div>
                    ))}
                  </div>
                </div>
              )}
              {isLoadingTemplates && (
                <div className="flex items-center gap-2 text-xs text-muted-foreground">
                  <Icon name="Loader2" size={12} className="animate-spin" />
                  {t('Loading...')}
                </div>
              )}

              <div>
                <label className="block text-sm font-medium text-muted-foreground mb-2">{t('Employment Type')}</label>
                <Select
                  value={formData.employmentType}
                  onChange={(value: EmploymentType) => setFormData({ ...formData, employmentType: value })}
                  options={employmentTypeOptions}
                />
              </div>
            </div>
          </div>

          {/* Short Leave Settings - commented out for later reference
          <div className="border border-teal-200 bg-teal-50 rounded-lg p-4">
            <h4 className="text-sm font-semibold text-foreground mb-4 flex items-center gap-2">
              <Icon name="Timer" size={16} />
              {t('shortLeaveSettings')}
            </h4>

            <div className="space-y-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-muted-foreground mb-2">{t('Monthly Short Leave Hours Limit')}</label>
                  <input
                    type="number"
                    min={0}
                    step={0.5}
                    value={formData.monthlyShortLeaveHoursLimit ?? 0}
                    onChange={(e) => setFormData({ ...formData, monthlyShortLeaveHoursLimit: parseFloat(e.target.value) || 0 })}
                    className="w-full px-3 py-2 border border-border rounded-md bg-card text-foreground focus-visible:ring-2 focus-visible:ring-primary/20 focus-visible:border-primary focus:outline-none"
                    data-testid="policy-monthly-short-leave-hours"
                  />
                  <p className="text-xs text-muted-foreground mt-1">{t('monthlyShortLeaveHoursHint')}</p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-muted-foreground mb-2">{t('maxShortLeaveHoursPerRequest')}</label>
                  <input
                    type="number"
                    min={0}
                    step={0.5}
                    value={formData.maxShortLeaveHoursPerRequest ?? 0}
                    onChange={(e) => setFormData({ ...formData, maxShortLeaveHoursPerRequest: parseFloat(e.target.value) || 0 })}
                    className="w-full px-3 py-2 border border-border rounded-md bg-card text-foreground focus-visible:ring-2 focus-visible:ring-primary/20 focus-visible:border-primary focus:outline-none"
                    data-testid="policy-max-short-leave-hours-per-request"
                  />
                  <p className="text-xs text-muted-foreground mt-1">{t('maxShortLeaveHoursPerRequestHint')}</p>
                </div>
              </div>

              <div className="border-t border-teal-200 pt-4 grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-muted-foreground mb-2">{t('shortLeaveNoticePeriodHours')}</label>
                  <input
                    type="number"
                    min={0}
                    value={formData.shortLeaveNoticePeriodHours ?? 0}
                    onChange={(e) => setFormData({ ...formData, shortLeaveNoticePeriodHours: parseInt(e.target.value) || 0 })}
                    className="w-full px-3 py-2 border border-border rounded-md bg-card text-foreground focus-visible:ring-2 focus-visible:ring-primary/20 focus-visible:border-primary focus:outline-none"
                    data-testid="policy-short-leave-notice-period"
                  />
                  <p className="text-xs text-muted-foreground mt-1">{t('shortLeaveNoticePeriodHoursHint')}</p>
                </div>

                <div className="flex items-start pt-7">
                  <label className="flex items-center">
                    <input
                      type="checkbox"
                      checked={formData.shortLeaveRequiresApproval ?? true}
                      onChange={(e) => setFormData({ ...formData, shortLeaveRequiresApproval: e.target.checked })}
                      className="me-2"
                      data-testid="policy-short-leave-requires-approval"
                    />
                    <div>
                      <span className="text-sm font-medium text-foreground">{t('shortLeaveRequiresApproval')}</span>
                      <p className="text-xs text-muted-foreground">{t('shortLeaveRequiresApprovalHint')}</p>
                    </div>
                  </label>
                </div>
              </div>

              <div className="border-t border-teal-200 pt-4">
                <label className="block text-sm font-medium text-muted-foreground mb-3">{t('shortLeaveAllowedTimeWindow')}</label>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-xs font-medium text-muted-foreground mb-1">{t('Start Time')}</label>
                    <input
                      type="time"
                      value={formData.shortLeaveWindowStart ?? ''}
                      onChange={(e) => setFormData({ ...formData, shortLeaveWindowStart: e.target.value || undefined })}
                      className="w-full px-3 py-2 border border-border rounded-md bg-card text-foreground focus-visible:ring-2 focus-visible:ring-primary/20 focus-visible:border-primary focus:outline-none"
                      data-testid="policy-short-leave-window-start"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-muted-foreground mb-1">{t('End Time')}</label>
                    <input
                      type="time"
                      value={formData.shortLeaveWindowEnd ?? ''}
                      onChange={(e) => setFormData({ ...formData, shortLeaveWindowEnd: e.target.value || undefined })}
                      className="w-full px-3 py-2 border border-border rounded-md bg-card text-foreground focus-visible:ring-2 focus-visible:ring-primary/20 focus-visible:border-primary focus:outline-none"
                      data-testid="policy-short-leave-window-end"
                    />
                  </div>
                </div>
                <p className="text-xs text-muted-foreground mt-1">{t('shortLeaveAllowedTimeWindowHint')}</p>
              </div>
            </div>
          </div>
          */}

          <div className="border-t border-border pt-4">
            <label className="flex items-center">
              <input
                type="checkbox"
                checked={formData.isDefault}
                onChange={(e) => setFormData({ ...formData, isDefault: e.target.checked })}
                className="me-2"
              />
              <div>
                <span className="text-sm font-medium text-foreground">{t('Set as Default Policy')}</span>
                <p className="text-xs text-muted-foreground">{t('This policy will be automatically applied to new employees')}</p>
              </div>
            </label>
          </div>
        </DialogBody>

        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={isSaving} data-testid="policy-cancel-btn">{t('Cancel')}</Button>
          <Button variant="default" onClick={handleSubmit} loading={isSaving} data-testid="policy-save-btn">
            {editingPolicy ? t('Update Policy') : t('Create Policy')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default PolicyFormModal;
